import axios from "axios";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import ESIInclude from "@JohnLewisPartnership/jl-esi-components";
import Breadcrumb from "@/components/breadcrumb/Breadcrumb";
import LocationFinder from "@/components/location-finder/LocationFinder";
import analyticsTrigger, { dispatchNewEvent } from "@/utils/analytics/trigger/analyticsTrigger";
import esiSourceWrapper from "@/constants/esiSourceWrapper";
import styles from "./index.module.scss";

const clickAndCollectClient = (params, isServer) => {
  const endpoint = isServer
    ? `http://${process.env.NEXT_PUBLIC_ENVIRONMENT_NAME}-collection-points/search`
    : `${process.env.NEXT_PUBLIC_API_DOMAIN}/customer-help/collection-points/v1/search`;

  return axios.get(endpoint, { params }).then(response => response && response.data);
};

const getAnalyticsSearchDetail = (searchTermHasChanged, limitHasChanged, includesExtendedNetwork) => {
  const analyticsSearchDetail = {
    action: "clickAndCollect.Event",
    type: "search",
  };
  if (searchTermHasChanged) {
    return analyticsSearchDetail;
  }
  if (limitHasChanged) {
    return {
      ...analyticsSearchDetail,
      subType: "showMore",
    };
  }
  if (includesExtendedNetwork) {
    return {
      ...analyticsSearchDetail,
      subType: "collectPlus",
    };
  }
  return analyticsSearchDetail;
};

export const metadata = {
  title: `Click and Collect | Customer Services`,
  description: `Find all the information you need regarding delivery and collection, including tracking your order, click and collect and our delivery services.`,
};

function ClickAndCollect({ currentSearchTerm, results, error }) {
  const router = useRouter();

  const handleLocationSearch = (searchTerm, searchLimit, searchExtendedNetwork = true) => {
    const { ...prevQuery } = router.query;
    const query = { ...prevQuery, q: searchTerm };
    if (searchLimit) {
      query.limit = searchLimit;
    }
    if (typeof searchExtendedNetwork !== "undefined") {
      if (Boolean(prevQuery?.includeExtendedNetwork) !== searchExtendedNetwork) {
        query.includeExtendedNetwork = searchExtendedNetwork;
      }
    }

    dispatchNewEvent("dataStateChange", {
      ...getAnalyticsSearchDetail(
        searchTerm !== currentSearchTerm,
        searchLimit && results?.length !== searchLimit,
        searchExtendedNetwork,
      ),
    });

    router.push({ pathname: router.pathname, query });
  };

  useEffect(() => {
    analyticsTrigger("dataStateChange", router.pathname, {}, "pageload");
  }, []);

  return (
    <>
      <Breadcrumb
        nativeLink
        links={[
          {
            text: "Delivery & collection",
            href: "/customer-services/delivery-information",
          },
        ]}
      />
      <ESIInclude
        src={esiSourceWrapper("/content-renderer/customer-help/delivery-information/click-and-collect-top")}
        id="content-top"
      />
      <div className="util-container">
        <div className={styles["click-and-collect__location-finder"]}>
          <LocationFinder
            locationSearch={handleLocationSearch}
            searchTerm={currentSearchTerm}
            results={results}
            error={error}
          />
        </div>
      </div>
      <ESIInclude
        src={esiSourceWrapper("/content-renderer/customer-help/delivery-information/click-and-collect-bottom")}
        id="content-bottom"
      />
    </>
  );
}

ClickAndCollect.propTypes = {
  currentSearchTerm: PropTypes.string,
  results: PropTypes.shape({
    collectionPoints: PropTypes.arrayOf(PropTypes.object),
    totalFound: PropTypes.number,
    length: PropTypes.number,
  }),
  error: PropTypes.objectOf(PropTypes.string),
};

ClickAndCollect.defaultProps = {
  currentSearchTerm: undefined,
  results: undefined,
  error: undefined,
};

ClickAndCollect.metadata = metadata;

ClickAndCollect.getInitialProps = async ({ query: { q, limit = 5, includeExtendedNetwork }, req }) => {
  const isServer = !!req;
  if (q) {
    try {
      const results = await clickAndCollectClient(
        { q, limit, includeExtendedNetwork: includeExtendedNetwork === "true" },
        isServer,
      );
      return {
        currentSearchTerm: q,
        includeExtendedNetwork: includeExtendedNetwork === "true",
        results,
      };
    } catch (error) {
      return {
        currentSearchTerm: q,
        includeExtendedNetwork: includeExtendedNetwork === "true",
        results: null,
        error: error.response ? error.response.data : { code: "request.error" },
      };
    }
  }
  return {};
};

export default ClickAndCollect;
